import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@altimateai/lego";
import {
  useCloneSubscription,
  useDeleteSubscription,
  useToggleSubscriptionStatus,
} from "../services/listApi";
import { AlertsTracking } from "@/lib/tracking/alerts";
import { SubscriptionStatus } from "@/pages/subscriptions/constants";

export const useSubscriptionActions = () => {
  const navigate = useNavigate();
  const deleteSubscription = useDeleteSubscription();
  const toggleStatus = useToggleSubscriptionStatus();
  const cloneSubscription = useCloneSubscription();

  const handleToggleStatus = useCallback(
    (id: number, currentStatus: SubscriptionStatus) => {
      const newStatus =
        currentStatus === SubscriptionStatus.ACTIVE
          ? SubscriptionStatus.INACTIVE
          : SubscriptionStatus.ACTIVE;
      const actionText = newStatus === SubscriptionStatus.ACTIVE ? "activated" : "deactivated";
      toggleStatus.mutate(
        { id: String(id), status: newStatus },
        {
          onSuccess: () => {
            AlertsTracking.ruleToggled(String(id), newStatus);
            toast({ title: "Success", description: `Subscription ${actionText} successfully` });
          },
          onError: (error: Error) => {
            toast({
              variant: "destructive",
              title: "Error",
              description: `Failed to ${actionText === "activated" ? "activate" : "deactivate"} subscription: ${error.message}`,
            });
          },
        }
      );
    },
    [toggleStatus]
  );

  const handleDelete = useCallback(
    (id: number) => {
      deleteSubscription.mutate(String(id), {
        onSuccess: () => {
          AlertsTracking.ruleDeleted(String(id));
          toast({ title: "Success", description: "Subscription deleted successfully" });
        },
        onError: (error: Error) => {
          toast({
            variant: "destructive",
            title: "Error",
            description: `Failed to delete subscription: ${error.message}`,
          });
        },
      });
    },
    [deleteSubscription]
  );

  const handleClone = useCallback(
    (id: number) => {
      cloneSubscription.mutate(String(id), {
        onSuccess: data => {
          AlertsTracking.ruleCloned(String(id));
          toast({ title: "Success", description: "Subscription cloned successfully" });
          if (data?.id) {
            navigate(`/subscriptions/${data.id}/edit`);
          }
        },
        onError: (error: Error) => {
          toast({
            variant: "destructive",
            title: "Error",
            description: `Failed to clone subscription: ${error.message}`,
          });
        },
      });
    },
    [cloneSubscription, navigate]
  );

  return { handleToggleStatus, handleDelete, handleClone };
};
